import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";

interface SlotTimesModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (
    slotStartTime: string,
    slotEndTime: string,
    startDate: string,
    noOfHours: string,
    saturdayHoliday: boolean,
    sundayHoliday: boolean
  ) => void;
  initialSlotStartTime?: string;
  initialSlotEndTime?: string;
  initialStartDate?: string;
  initialNoOfHours?: string;
  initialSaturdayHoliday?: boolean;
  initialSundayHoliday?: boolean;
}

const SlotTimesModal: React.FC<SlotTimesModalProps> = ({
  show,
  onHide,
  onSave,
  initialSlotStartTime,
  initialSlotEndTime,
  initialStartDate,
  initialNoOfHours,
  initialSaturdayHoliday,
  initialSundayHoliday
}) => {
  const [slotStartTime, setSlotStartTime] = useState<string>("");
  const [slotEndTime, setSlotEndTime] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [noOfHours, setNoOfHours] = useState<string>("");
  const [saturdayHoliday, setSaturdayHoliday] = useState<boolean>(false);
  const [sundayHoliday, setSundayHoliday] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // Calculate today's date at midnight UTC once per render cycle
  const todayUTC = React.useMemo(() => {
    const today = new Date();
    return new Date(Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      0, 0, 0, 0
    ));
  }, []);

  // Check if modal should be disabled based on project start date
  // If project start date is less than or equal to today, disable the modal
  // If project start date is greater than today, allow changes
  const isDisabled = React.useMemo(() => {
    if (!startDate) {
      // If no start date is set, allow changes
      return false;
    }
    
    try {
      // Parse the start date (format: YYYY-MM-DD)
      const [year, month, day] = startDate.split('-').map(Number);
      const projectStartDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
      
      // Disable if project start date is less than or equal to today
      return projectStartDate <= todayUTC;
    } catch (error) {
      // If parsing fails, allow changes
      return false;
    }
  }, [startDate, todayUTC]);

  React.useEffect(() => {
    if (show) {
      // Convert initial times to datetime-local format if provided
      if (initialSlotStartTime) {
        try {
          const date = new Date(initialSlotStartTime);
          const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
          setSlotStartTime(localDateTime);
        } catch (e) {
          setSlotStartTime("");
        }
      } else {
        setSlotStartTime("");
      }

      if (initialSlotEndTime) {
        try {
          const date = new Date(initialSlotEndTime);
          const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
          setSlotEndTime(localDateTime);
        } catch (e) {
          setSlotEndTime("");
        }
      } else {
        setSlotEndTime("");
      }

      // Common batch settings - use provided initial values
      setStartDate(initialStartDate || "");
      setNoOfHours(initialNoOfHours || "");
      setSaturdayHoliday(initialSaturdayHoliday ?? false);
      setSundayHoliday(initialSundayHoliday ?? false);
      setError("");
    }
  }, [show, initialSlotStartTime, initialSlotEndTime, initialStartDate, initialNoOfHours, initialSaturdayHoliday, initialSundayHoliday]);

  const handleSave = () => {
    if (!slotStartTime || !slotEndTime) {
      setError("Please fill in both slot start time and slot end time");
      return;
    }

    if (!startDate) {
      setError("Please select a start date");
      return;
    }

    const hoursNum = parseFloat(noOfHours || "0");
    if (!noOfHours || isNaN(hoursNum) || hoursNum <= 0) {
      setError("Please enter a valid 'No of Hours' (> 0)");
      return;
    }

    try {
      // Parse datetime-local string directly as UTC (format: YYYY-MM-DDTHH:mm)
      // Treat the selected time as UTC time, not local time
      const parseAsUTC = (dateTimeString: string): Date => {
        // datetime-local format is "YYYY-MM-DDTHH:mm"
        const [datePart, timePart] = dateTimeString.split('T');
        if (!datePart || !timePart) {
          throw new Error("Invalid datetime format");
        }
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        
        // Create date in UTC directly
        return new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
      };

      const slotStartDateTime = parseAsUTC(slotStartTime);
      const slotEndDateTime = parseAsUTC(slotEndTime);

      if (isNaN(slotStartDateTime.getTime()) || isNaN(slotEndDateTime.getTime())) {
        setError("Invalid date format");
        return;
      }

      // Validation 1: Slot End Date & Time must be greater than Slot Start Date & Time
      if (slotEndDateTime <= slotStartDateTime) {
        setError("Slot end date and time must be after slot start date and time");
        return;
      }

      // Parse start date (format: YYYY-MM-DD)
      const parseStartDate = (dateString: string): Date => {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
      };

      const projectStartDate = parseStartDate(startDate);
      
      if (isNaN(projectStartDate.getTime())) {
        setError("Invalid start date format");
        return;
      }

      // Validation 2: Start Date must be greater than Slot End Date (not less than or equal)
      // Compare only dates (ignore time)
      const slotEndDateOnly = new Date(Date.UTC(
        slotEndDateTime.getUTCFullYear(),
        slotEndDateTime.getUTCMonth(),
        slotEndDateTime.getUTCDate(),
        0, 0, 0, 0
      ));

      const projectStartDateOnly = new Date(Date.UTC(
        projectStartDate.getUTCFullYear(),
        projectStartDate.getUTCMonth(),
        projectStartDate.getUTCDate(),
        0, 0, 0, 0
      ));

      if (projectStartDateOnly <= slotEndDateOnly) {
        setError("Start date must be after slot end date");
        return;
      }

      // Format as the actual selected time (not UTC conversion)
      // datetime-local format is "YYYY-MM-DDTHH:mm", just add ":00" for seconds
      const formatAsSelectedTime = (dateTimeString: string): string => {
        // datetime-local format is "YYYY-MM-DDTHH:mm"
        // Just add ":00" for seconds, keep the time as selected
        return `${dateTimeString}:00`;
      };

      const startISO = formatAsSelectedTime(slotStartTime);
      const endISO = formatAsSelectedTime(slotEndTime);

      onSave(startISO, endISO, startDate, noOfHours, saturdayHoliday, sundayHoliday);
    } catch (error) {
      setError("Error processing dates");
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Batch Mapping Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}

        <Form.Group className="mb-3">
          <Form.Label>Project Start Date</Form.Label>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            disabled={isDisabled}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Number of working hours (per day)</Form.Label>
          <Form.Control
            type="number"
            min="0"
            value={noOfHours}
            onChange={(e) => setNoOfHours(e.target.value)}
            required
            disabled={isDisabled}
          />
        </Form.Group>
        <Form.Group className="mb-3 d-flex gap-4">
          <Form.Check
            type="checkbox"
            label="Saturday Holiday"
            checked={saturdayHoliday}
            onChange={(e) => setSaturdayHoliday(e.target.checked)}
            disabled={isDisabled}
          />
          <Form.Check
            type="checkbox"
            label="Sunday Holiday"
            checked={sundayHoliday}
            onChange={(e) => setSundayHoliday(e.target.checked)}
            disabled={isDisabled}
          />
        </Form.Group>
        <hr />
        <Form.Group className="mb-3">
          <Form.Label>Slot Start Time (Date & Time)</Form.Label>
          <Form.Control
            type="datetime-local"
            value={slotStartTime}
            onChange={(e) => setSlotStartTime(e.target.value)}
            required
            disabled={isDisabled}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Slot End Time (Date & Time)</Form.Label>
          <Form.Control
            type="datetime-local"
            value={slotEndTime}
            onChange={(e) => setSlotEndTime(e.target.value)}
            required
            disabled={isDisabled}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Continue
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SlotTimesModal;

